import type { AgentRunInputState } from '../../contracts/harness.types';
import { compareStreamIds } from './stream/stream-ids';

/**
 * This client's view of the active turn, as an explicit state machine.
 *
 * It exists to reconcile three sources of truth that disagree at turn
 * boundaries: the polled run snapshot (`input_state`, laggy), the live SSE
 * stream (fast, ephemeral), and local intent (submit / cancel). Tracking these
 * as separate booleans bred bugs — e.g. a turn the stream reported as finished
 * still read `turn_running` from the stale snapshot for a poll cycle, leaving
 * the Stop button live and firing doomed cancels.
 *
 * The phases:
 * - `idle`       — no locally-driven turn; the snapshot is authoritative
 *                  (covers turns started elsewhere, e.g. a schedule).
 * - `submitting` — we POSTed a turn but the snapshot hasn't yet confirmed
 *                  `turn_running`. This is the post-submit lag window, where
 *                  the snapshot still reads the PRIOR settled state — so it must
 *                  NOT be collapsed by a settled snapshot (that would drop a
 *                  turn that's about to start).
 * - `running`    — the snapshot has confirmed the turn is active. Because the
 *                  lag window is over, a later settled snapshot (RECONCILED)
 *                  CAN safely collapse this even if the stream never delivered a
 *                  boundary (e.g. a non-retryable SSE failure at turn end) —
 *                  this is what keeps `running` from stranding.
 * - `cancelling` — we POSTed a cancel and are awaiting the boundary/reconcile.
 * - `ended`      — the stream reported a turn boundary; the snapshot may still
 *                  read `turn_running` for a cycle. This phase OVERRIDES the
 *                  stale snapshot so we stop offering Stop / opening the stream
 *                  the instant we know the turn is over. It records the boundary
 *                  event's stream cursor (`endedAtCursor`); the suppression
 *                  lifts once the snapshot's OWN `stream_cursor` catches up past
 *                  it, at which point the snapshot authoritatively reflects the
 *                  boundary — so an active reading there is a genuinely NEW turn
 *                  (a scheduler/other tab chaining the next one) and escapes to
 *                  `running`, while a settled reading reconciles to idle.
 */
export type TurnPhase = 'idle' | 'submitting' | 'running' | 'cancelling' | 'ended';

export interface TurnLifecycleState {
  phase: TurnPhase;
  /**
   * The boundary event's stream cursor captured on entering `ended`. The
   * `ended` suppression holds while the snapshot's `stream_cursor` is behind
   * this, and lifts once it catches up. Using the always-present event id (vs
   * the optional `metadata.turn_id`) means boundaries without a turn id still
   * discriminate the stale just-ended turn from a new one. Null outside `ended`.
   */
  endedAtCursor: string | null;
}

export type TurnLifecycleAction =
  /** User submitted a turn (optimistic); enters the post-submit lag window. */
  | { type: 'SUBMIT' }
  /** The optimistic turn's POST was rejected; leave the lag window. */
  | { type: 'SUBMIT_REJECTED' }
  /**
   * Snapshot reports an active turn (`turn_running`/`approval_required`).
   * `streamCursor` is the snapshot's own cursor, compared against
   * `endedAtCursor` to tell a genuinely new turn from the stale just-ended one.
   */
  | { type: 'TURN_CONFIRMED'; streamCursor: string | null }
  /** Cancel POST accepted; awaiting the `turn_cancelled` boundary. */
  | { type: 'CANCEL_REQUESTED' }
  /** Stream delivered a turn boundary; `cursor` is the boundary event id. */
  | { type: 'BOUNDARY'; cursor: string | null }
  /** Snapshot settled out of an active state, confirming the turn is over. */
  | { type: 'RECONCILED' }
  /** Cancel never confirmed within the timeout; fall back to running. */
  | { type: 'CANCEL_TIMEOUT' }
  /**
   * The run reached `failed`/`closed`. It will never confirm or stream a turn,
   * so no phase — not even `submitting` — may outlive it.
   */
  | { type: 'RUN_ENDED' }
  /** The run id changed; reset to a clean slate. */
  | { type: 'RUN_CHANGED' };

export const INITIAL_TURN_LIFECYCLE: TurnLifecycleState = {
  phase: 'idle',
  endedAtCursor: null,
};

const RUNNING: TurnLifecycleState = { phase: 'running', endedAtCursor: null };
const CANCELLING: TurnLifecycleState = { phase: 'cancelling', endedAtCursor: null };

/**
 * Has the snapshot caught up to/past the boundary cursor? Once it has, the
 * snapshot authoritatively reflects the boundary, so its `input_state` can be
 * trusted again (active ⇒ new turn, settled ⇒ over).
 */
const snapshotCaughtUp = (
  streamCursor: string | null,
  endedAtCursor: string | null
): boolean =>
  streamCursor !== null &&
  endedAtCursor !== null &&
  compareStreamIds(streamCursor, endedAtCursor) >= 0;

/**
 * A snapshot at the old boundary still only contains that ending. Confirming a
 * post-submit turn requires strictly newer cursor evidence.
 */
const snapshotPastBoundary = (
  streamCursor: string | null,
  endedAtCursor: string | null
): boolean =>
  streamCursor !== null &&
  endedAtCursor !== null &&
  compareStreamIds(streamCursor, endedAtCursor) > 0;

export const turnLifecycleReducer = (
  state: TurnLifecycleState,
  action: TurnLifecycleAction
): TurnLifecycleState => {
  switch (action.type) {
    case 'SUBMIT':
      // A fresh turn supersedes the phase. Keep the just-ended cursor so a
      // cached snapshot of that ending cannot confirm this submit.
      return {
        phase: 'submitting',
        endedAtCursor: state.phase === 'ended' ? state.endedAtCursor : null,
      };
    case 'SUBMIT_REJECTED':
      // Only `submitting` is ours to undo — from any other phase a boundary, a
      // cancel, or a newer submit has already superseded this turn.
      return state.phase === 'submitting' ? INITIAL_TURN_LIFECYCLE : state;
    case 'TURN_CONFIRMED':
      // The snapshot caught up to our optimistic submit; leave the lag window.
      // A carried boundary means this snapshot may still be the prior turn —
      // only a strictly newer cursor can confirm the submit.
      if (state.phase === 'submitting') {
        if (
          state.endedAtCursor === null ||
          snapshotPastBoundary(action.streamCursor, state.endedAtCursor)
        ) {
          return RUNNING;
        }
        return state;
      }
      // In `ended`: once the snapshot's cursor reaches the boundary yet still
      // reads active, this is a genuinely new turn (the stale just-ended turn
      // would read settled by then) — escape so we stream it and offer Stop.
      if (
        state.phase === 'ended' &&
        snapshotCaughtUp(action.streamCursor, state.endedAtCursor)
      ) {
        return RUNNING;
      }
      return state;
    case 'CANCEL_REQUESTED':
      // Reachable from `submitting`/`running` (our own turn) and from `idle`
      // (a turn started elsewhere that we're cancelling — without this, a
      // snapshot-driven cancel never sets `cancelling`, so the Stop button
      // stays live and repeated clicks issue duplicate cancels).
      return state.phase === 'submitting' ||
        state.phase === 'running' ||
        state.phase === 'idle'
        ? CANCELLING
        : state;
    case 'BOUNDARY':
      // The turn is over regardless of which phase we were in (including a
      // remote turn we were only streaming) — hold `ended` until the snapshot
      // catches up so the stale `input_state` can't resurrect Stop/stream.
      // Record the boundary cursor so a new turn can later escape `ended`.
      return { phase: 'ended', endedAtCursor: action.cursor };
    case 'RECONCILED':
      // The snapshot is authoritative once it settles out of an active state.
      // Collapse `ended`/`cancelling` (waiting on the snapshot) AND `running`
      // (confirmed-active turn that has now ended — covers a boundary-less
      // stream failure). NOT `submitting`: there the snapshot still lags the
      // optimistic submit, so a settled reading is stale, not terminal.
      return state.phase === 'ended' ||
        state.phase === 'cancelling' ||
        state.phase === 'running'
        ? INITIAL_TURN_LIFECYCLE
        : state;
    case 'CANCEL_TIMEOUT':
      // Backend never confirmed the cancel; treat the turn as still running so
      // the user can retry rather than being stranded on "Stopping…".
      return state.phase === 'cancelling' ? RUNNING : state;
    case 'RUN_ENDED':
    case 'RUN_CHANGED':
      return INITIAL_TURN_LIFECYCLE;
    default:
      return state;
  }
};

const isSnapshotActive = (inputState: AgentRunInputState | undefined): boolean =>
  inputState === 'turn_running' || inputState === 'approval_required';

/**
 * Is a turn active from this client's perspective? Drives the composer's Stop
 * affordance and whether the SSE stream should be open. In `ended` this is
 * false even when the snapshot still reads `turn_running` — that override is
 * the whole point of the machine.
 */
export const isTurnInFlight = (
  phase: TurnPhase,
  inputState: AgentRunInputState | undefined
): boolean =>
  phase === 'submitting' ||
  phase === 'running' ||
  phase === 'cancelling' ||
  (phase === 'idle' && isSnapshotActive(inputState));

/**
 * May we POST a cancel right now? Excludes `ended` (the turn already finished,
 * snapshot just hasn't caught up) and `cancelling` (already in flight). Allows
 * `idle` + any active snapshot so a turn started elsewhere is still cancellable.
 *
 * `approval_required` counts as active here, matching `isTurnInFlight`: a turn
 * parked on an approval has not finished, and the backend's cancel path resolves
 * the pending approvals and writes a proper tool result for the dangling
 * `tool_use`.
 */
export const isTurnCancellable = (
  phase: TurnPhase,
  inputState: AgentRunInputState | undefined
): boolean =>
  phase === 'submitting' ||
  phase === 'running' ||
  (phase === 'idle' && isSnapshotActive(inputState));
